import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchStationSnapshot } from "@/lib/api";
import { Mascot } from "@/components/Mascot";
import { OfficialMeteocat } from "@/components/OfficialMeteocat";
import { toast } from "sonner";
import {
  ArrowLeft, Snowflake, Wind, Droplets, Cloud, Gauge, Compass, Eye, Sun,
  Star, GitCompareArrows, Mountain, Sparkles,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart, CartesianGrid,
} from "recharts";
import { isFavorite, toggleFavorite, getCompareIds, toggleCompare } from "@/lib/storage";

const fmtHour = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit" });
  } catch {
    return "";
  }
};
const fmtDay = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("es-ES", { weekday: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

export default function StationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fav, setFav] = useState(isFavorite(id));
  const [inCompare, setInCompare] = useState(getCompareIds().includes(id));

  useEffect(() => {
    let mounted = true;
    const CACHE_KEY = `creep_station_snapshot_${id}`;
    const MAX_STALE_MS = 24 * 60 * 60 * 1000; // 24h

    // 1) Hidratar desde cache local si existe (evita pantalla en blanco).
    //    Sólo hidratamos si el snapshot cacheado estaba disponible; si
    //    la última vez estaba "no disponible" preferimos esperar al fetch
    //    fresco antes que mostrar la pantalla de "no disponible".
    let hadCache = false;
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const age = Date.now() - (parsed._cachedAt || 0);
        if (parsed._data && age < MAX_STALE_MS && parsed._data.available !== false) {
          setSnap({ ...parsed._data, _stale: true });
          setLoading(false);
          hadCache = true;
        } else if (parsed._data && parsed._data.available === false) {
          // Purga la entrada caducada para que no vuelva a interferir
          try { localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
        }
      }
    } catch { /* cache corrupta */ }

    if (!hadCache) setLoading(true);
    setError(null);

    fetchStationSnapshot(id)
      .then((d) => {
        if (!mounted) return;
        setSnap((prev) => {
          // Si el backend responde "no disponible" pero teníamos datos
          // válidos previos, mantenemos los previos marcados como stale.
          if (d?.available === false && prev && prev.available !== false) {
            return { ...prev, _stale: true };
          }
          try {
            localStorage.setItem(
              CACHE_KEY,
              JSON.stringify({ _data: d, _cachedAt: Date.now() }),
            );
          } catch { /* quota */ }
          return d;
        });
      })
      .catch((e) => {
        if (!mounted) return;
        console.error("fetchStationSnapshot failed:", e);
        const status = e?.response?.status;
        if (status === 404) {
          // ID desconocido (probablemente Andorra retirada o favorito viejo).
          // Purgamos las trazas locales y llevamos al usuario al inicio.
          try {
            localStorage.removeItem(CACHE_KEY);
            const favs = JSON.parse(localStorage.getItem("creep.favorites.v1") || "[]");
            localStorage.setItem(
              "creep.favorites.v1",
              JSON.stringify(favs.filter((f) => f !== id)),
            );
            const cmp = JSON.parse(localStorage.getItem("creep.compare.v1") || "[]");
            localStorage.setItem(
              "creep.compare.v1",
              JSON.stringify(cmp.filter((c) => c !== id)),
            );
          } catch { /* noop */ }
          toast("Esa estación ya no está activa. Te llevo al inicio.", {
            description: "Hemos limpiado favoritos y comparador.",
          });
          navigate("/", { replace: true });
          return;
        }
        // Sólo mostrar error si NO tenemos snapshot cacheado
        if (!hadCache) setError(e?.message || "No hemos podido cargar la estación.");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id, navigate]);

  useEffect(() => {
    const onF = () => setFav(isFavorite(id));
    const onC = () => setInCompare(getCompareIds().includes(id));
    window.addEventListener("creep:favorites", onF);
    window.addEventListener("creep:compare", onC);
    return () => {
      window.removeEventListener("creep:favorites", onF);
      window.removeEventListener("creep:compare", onC);
    };
  }, [id]);

  if (loading) {
    return (
      <div className="creep-glass p-10 text-center" data-testid="station-loading">
        <Mascot variant="alert" size={140} />
        <div className="mt-4 text-white/70">Invocando el aura de la estación…</div>
      </div>
    );
  }

  if (error || !snap) {
    return (
      <div className="creep-glass p-10 text-center" data-testid="station-error">
        <Mascot variant="alert" size={140} />
        <div className="mt-4 text-lg font-semibold text-white">No hemos podido cargar la estación</div>
        <div className="mt-2 text-sm text-white/60 max-w-md mx-auto">
          {error || "El backend no ha respondido. Puede que se haya reiniciado."}
        </div>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            data-testid="station-retry"
            onClick={() => {
              setError(null);
              setLoading(true);
              const CACHE_KEY = `creep_station_snapshot_${id}`;
              fetchStationSnapshot(id)
                .then((d) => {
                  setSnap(d);
                  try {
                    localStorage.setItem(
                      CACHE_KEY,
                      JSON.stringify({ _data: d, _cachedAt: Date.now() }),
                    );
                  } catch { /* quota */ }
                })
                .catch((e) => setError(e?.message || "Error al recargar"))
                .finally(() => setLoading(false));
            }}
            className="creep-btn-primary text-sm !py-2 !px-4"
          >
            Reintentar
          </button>
          <Link
            to="/"
            data-testid="station-error-back"
            className="creep-btn-ghost inline-flex items-center gap-2 text-sm !py-2 !px-4"
          >
            <ArrowLeft size={14} /> Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  const { station, weather, hourly_forecast, daily_forecast, alerts, ski, confidence, sources, webcams = [], season, risks = [], available = true, unavailable_reason } = snap;

  // Early return for stations without data (Andorra until API is connected)
  if (!available) {
    return (
      <div data-testid={`station-detail-${station.id}`}>
        <div className="flex items-center justify-between mb-4">
          <Link to="/" data-testid="back-link" className="creep-btn-ghost inline-flex items-center gap-2 text-sm !py-1.5 !px-3">
            <ArrowLeft size={14} /> Volver
          </Link>
        </div>
        <section className="relative creep-glass-strong overflow-hidden mb-6">
          <div className="absolute inset-0">
            <img src={station.image} alt="" className="w-full h-full object-cover opacity-25 grayscale" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/85" />
          </div>
          <div className="relative p-6 sm:p-10">
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300/80">{station.region}</div>
            <h1 className="mt-1.5 text-3xl sm:text-4xl font-extrabold tracking-tight">{station.name}</h1>
            <div className="mt-6 inline-block px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-100 text-xs font-semibold uppercase tracking-widest">
              No disponible
            </div>
            <p className="mt-4 text-white/70 max-w-lg text-sm leading-relaxed">
              {unavailable_reason || "No hay una fuente de datos meteorológica disponible ahora mismo para esta estación."}
            </p>
            <p className="mt-2 text-white/50 max-w-lg text-xs leading-relaxed">
              La fuente actual es <span className="text-white/80">Meteocat</span>. En cuanto vuelva a responder los datos aparecerán aquí de forma automática.
            </p>
          </div>
        </section>
      </div>
    );
  }

  const hourlyChartData = (hourly_forecast || []).map((h) => ({
    hour: fmtHour(h.time),
    temp: h.temperature,
    hum: h.humidity,
    wind: h.wind_speed,
  }));
  const dailyChartData = (daily_forecast || []).map((d) => ({
    day: fmtDay(d.date),
    max: d.temp_max,
    min: d.temp_min,
    wind: d.wind_speed_max,
  }));
  const hasHourly = hourlyChartData.length > 0;
  const hasDaily = dailyChartData.length > 0;

  return (
    <div data-testid={`station-detail-${station.id}`}>
      <div className="flex items-center justify-between mb-4">
        <Link to="/" data-testid="back-link" className="creep-btn-ghost inline-flex items-center gap-2 text-sm !py-1.5 !px-3">
          <ArrowLeft size={14} /> Volver
        </Link>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="detail-fav-btn"
            aria-label="Favorito"
            onClick={() => toggleFavorite(station.id)}
            className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
              fav
                ? "bg-violet-500/25 border-violet-400 text-violet-100"
                : "bg-white/[0.04] border-white/10 text-white/70 active:text-white"
            }`}
          >
            <Star size={15} fill={fav ? "currentColor" : "none"} />
          </button>
          <button
            data-testid="detail-compare-btn"
            aria-label="Comparar"
            onClick={() => toggleCompare(station.id)}
            className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors ${
              inCompare
                ? "bg-violet-500/25 border-violet-400 text-violet-100"
                : "bg-white/[0.04] border-white/10 text-white/70 active:text-white"
            }`}
          >
            <GitCompareArrows size={15} />
          </button>
        </div>
      </div>

      {/* Header hero — mobile compact */}
      <section className="relative creep-glass-strong overflow-hidden mb-6">
        <div className="absolute inset-0">
          <img src={station.image} alt="" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/85" />
        </div>
        <div className="relative grid grid-cols-[1fr_auto] gap-4 sm:gap-8 p-5 sm:p-8 items-end">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] text-violet-300/70">{station.region}</div>
            <h1 className="mt-1.5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight truncate">
              {station.name}
            </h1>
            <div className="text-white/55 text-[11px] sm:text-xs mt-1.5">
              {station.altitude_base}–{station.altitude_top} m · {station.total_km} km
            </div>
            <div className="mt-4 flex items-end gap-4">
              <div>
                <div className="flex items-baseline">
                  <span className="text-5xl sm:text-6xl font-extrabold">
                    {weather.temperature != null ? Math.round(weather.temperature) : "—"}
                  </span>
                  <span className="text-xl text-white/60 ml-1">°C</span>
                </div>
                <div className="text-xs text-white/60 mt-0.5">
                  {weather.sky} · Sens. {Math.round(weather.feels_like)}°
                </div>
              </div>
              <div className="text-[11px] text-white/50 pb-1 leading-tight">
                <div>Máx <span className="text-white">{weather.temp_max != null ? `${Math.round(weather.temp_max)}°` : "—"}</span></div>
                <div>Mín <span className="text-white">{weather.temp_min != null ? `${Math.round(weather.temp_min)}°` : "—"}</span></div>
              </div>
            </div>
          </div>
          <div className="justify-self-end shrink-0">
            <Mascot
              variant={alerts.length > 0 ? "alert" : ski?.skiable ? "celebrate" : "hero"}
              size={110}
            />
          </div>
        </div>
      </section>

      {/* Alerts strip */}
      {alerts.length > 0 && (
        <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="detail-alerts">
          {alerts.map((a, i) => (
            <div
              key={i}
              className={`rounded-2xl p-4 border ${
                a.level === "danger"
                  ? "border-red-500/40 bg-red-500/10 text-red-100"
                  : a.level === "warning"
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-100"
                  : "border-violet-500/40 bg-violet-500/10 text-violet-100"
              }`}
            >
              <div className="text-[10px] uppercase tracking-wider opacity-70">Alerta</div>
              <div className="font-semibold mt-0.5">{a.title}</div>
              <div className="text-xs opacity-80 mt-1">{a.message}</div>
            </div>
          ))}
        </div>
      )}

      {/* Data quality strip */}
      <section className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="data-quality-strip">
        {ski && (
          <div className="creep-glass p-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6BFF]">
              {ski.skiable ? "Se puede esquiar" : "No esquiable hoy"}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <div className="text-4xl font-extrabold text-white">{ski.score}</div>
              <div className="text-sm text-white/70">/ 100 · {ski.label}</div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(ski.reasons || []).map((r, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.04] text-white/70">{r}</span>
              ))}
            </div>
          </div>
        )}
        {confidence && (
          <div className="creep-glass p-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6BFF]">Confianza del dato</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full" style={{
                background: confidence === "Alta" ? "#5B2EFF" : confidence === "Media" ? "#8A6BFF" : "#D6002F",
                boxShadow: "0 0 12px currentColor",
              }} />
              <div className="text-lg font-bold text-white">{confidence}</div>
            </div>
            <div className="mt-2 text-[10px] text-white/60 leading-relaxed">
              Temperatura fusiona: <span className="text-white/85">{sources?.weather?.temperature || "—"}</span>
            </div>
            <div className="mt-1 text-[10px] text-white/50">
              Actualizado: {snap.updated_at ? new Date(snap.updated_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : "—"}
            </div>
          </div>
        )}
        {season && (
          <div className="creep-glass p-4" data-testid="season-panel">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6BFF]">Temporada</div>
            <div className="mt-1 text-lg font-bold text-white">{season.label}</div>
            <div className="mt-2 text-[11px] text-white/65">
              {season.key === "winter" && "Prioridad: condiciones frías, viento bajo, sin lluvia."}
              {season.key === "spring" && "Nieve primavera. Mejor esquiar por la mañana."}
              {season.key === "summer" && "Fuera de temporada de esquí · consulta senderismo y BTT."}
              {season.key === "autumn" && "Preparación de temporada · primeras nevadas."}
            </div>
          </div>
        )}
      </section>

      <OfficialMeteocat official={snap.official} snowDepth={weather.snow_depth_cm} />

      {/* Webcams row */}
      {webcams.length > 0 && (
        <section className="mb-8" data-testid="webcams-section">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6BFF] mb-2">Webcams oficiales</div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {webcams.map((w, i) => (
              <a
                key={i}
                data-testid={`webcam-${i}`}
                href={w.url}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 min-w-[200px] rounded-2xl p-3 border transition-colors flex items-center gap-3"
                style={{ background: "rgba(11,11,18,0.55)", borderColor: "rgba(138,107,255,0.25)" }}
              >
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(91,46,255,0.2)" }}>
                  <Sparkles size={14} className="text-[#8A6BFF]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{w.label}</div>
                  <div className="text-[10px] text-white/50">Abrir en la web oficial ↗</div>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Bento grid */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10">
        <Panel title="Meteorología" testId="panel-weather">
          <div className="grid grid-cols-2 gap-3">
            <Stat icon={Sun} label="UV" value={weather.uv_index ?? "—"} />
            <Stat icon={Droplets} label="Humedad" value={`${weather.humidity != null ? Math.round(weather.humidity) : "—"}%`} />
            <Stat icon={Gauge} label="Presión" value={`${Math.round(weather.pressure ?? 0)} hPa`} />
            <Stat icon={Cloud} label="Nubes" value={`${weather.cloud_cover ?? "—"}%`} />
            <Stat icon={Wind} label="Viento" value={`${Math.round(weather.wind_speed ?? 0)} km/h`} />
            <Stat icon={Compass} label="Dirección" value={weather.wind_direction_label} />
            <Stat icon={Eye} label="Visibilidad" value={`${weather.visibility_km} km`} />
            <Stat icon={Wind} label="Ráfagas" value={`${Math.round(weather.wind_gust ?? 0)} km/h`} />
          </div>
        </Panel>

        <Panel title="Montaña" testId="panel-mountain">
          <div className="grid grid-cols-2 gap-3">
            <Stat icon={Mountain} label="Cota de nieve" value={weather.freezing_level_m != null ? `${Math.round(weather.freezing_level_m)} m` : "—"} tint />
            <Stat icon={Mountain} label="Cota estación" value={`${station.altitude_top} m`} />
            <Stat icon={Cloud} label="Prob. lluvia" value={`${weather.precipitation_probability ?? 0}%`} />
            <Stat icon={Snowflake} label="Cielo" value={weather.sky || "—"} />
          </div>
          <div className="mt-4 text-[11px] text-white/60 leading-relaxed">
            {weather.freezing_level_m != null && station.altitude_top && (
              weather.freezing_level_m > station.altitude_top + 200
                ? "La cota 0°C está por encima del pico: cualquier precipitación sería lluvia."
                : weather.freezing_level_m < station.altitude_base
                ? "Todo el dominio está bajo la cota 0°C: precipitación en forma de nieve."
                : "Cota 0°C dentro del dominio: nieve en cotas altas, lluvia abajo."
            )}
          </div>
        </Panel>
      </section>

      {/* Hourly chart */}
      <section className="creep-glass-strong p-6 mb-6" data-testid="chart-hourly">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-bold text-xl tracking-tight">Próximas horas</h2>
            <div className="text-xs text-white/50">Temperatura y humedad por franjas de 3h</div>
          </div>
        </div>
        {hasHourly ? (
          <div style={{ width: "100%", height: 260, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} initialDimension={{ width: 320, height: 260 }}>
              <AreaChart data={hourlyChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="tempGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="hour" tick={{ fill: "#ffffff70", fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "#ffffff70", fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "rgba(10,10,15,0.9)", border: "1px solid rgba(124,58,237,0.4)", borderRadius: 12, color: "white" }} />
                <Area type="monotone" dataKey="temp" stroke="#C4B5FD" strokeWidth={2} fill="url(#tempGrad)" name="°C" />
                <Line type="monotone" dataKey="hum" stroke="#60A5FA" strokeWidth={1.5} dot={false} name="humedad %" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-10 text-sm text-white/50" data-testid="chart-hourly-empty">
            Predicción horaria no disponible ahora mismo · datos actuales en tiempo real desde XEMA.
          </div>
        )}
      </section>

      {/* Daily chart */}
      <section className="creep-glass-strong p-6" data-testid="chart-daily">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-bold text-xl tracking-tight">Próximos días</h2>
            <div className="text-xs text-white/50">Máximas y mínimas de temperatura</div>
          </div>
        </div>
        {hasDaily ? (
          <>
            <div style={{ width: "100%", height: 260, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} initialDimension={{ width: 320, height: 260 }}>
                <LineChart data={dailyChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#ffffff10" vertical={false} />
                  <XAxis dataKey="day" tick={{ fill: "#ffffff70", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fill: "#ffffff70", fontSize: 11 }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "rgba(10,10,15,0.9)", border: "1px solid rgba(124,58,237,0.4)", borderRadius: 12, color: "white" }} />
                  <Line type="monotone" dataKey="max" stroke="#F0ABFC" strokeWidth={2.2} dot={{ r: 3, fill: "#F0ABFC" }} name="máx" />
                  <Line type="monotone" dataKey="min" stroke="#93C5FD" strokeWidth={2.2} dot={{ r: 3, fill: "#93C5FD" }} name="mín" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {(daily_forecast || []).slice(0, 7).map((d, i) => (
                <div key={i} className="rounded-xl bg-white/[0.03] border border-white/8 p-3 text-center" data-testid={`daily-card-${i}`}>
                  <div className="text-[11px] text-white/60 capitalize">{fmtDay(d.date)}</div>
                  <div className="mt-2 text-sm">
                    <span className="font-semibold">{d.temp_max != null ? Math.round(d.temp_max) : "—"}°</span>
                    <span className="text-white/40"> / {d.temp_min != null ? Math.round(d.temp_min) : "—"}°</span>
                  </div>
                  <div className="text-[10px] mt-1 text-violet-300/80">
                    {d.wind_speed_max != null ? `viento ${Math.round(d.wind_speed_max)} km/h` : ""}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-10 text-sm text-white/50" data-testid="chart-daily-empty">
            Predicción diaria no disponible ahora mismo · volverá cuando Meteocat refresque su ventana.
          </div>
        )}
      </section>
    </div>
  );
}

const Panel = ({ title, children, testId }) => (
  <div className="creep-glass p-5" data-testid={testId}>
    <h2 className="text-[11px] uppercase tracking-[0.3em] text-violet-300/80 mb-4">{title}</h2>
    {children}
  </div>
);

const Stat = ({ icon: Icon, label, value, tint, danger }) => (
  <div
    className={`rounded-xl p-3 border transition-colors ${
      danger
        ? "bg-red-500/10 border-red-500/30 text-red-100"
        : tint
        ? "bg-violet-500/10 border-violet-500/25"
        : "bg-white/[0.03] border-white/8"
    }`}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/60">
      <Icon size={12} />
      {label}
    </div>
    <div className="mt-1.5 font-semibold text-lg text-white">{value}</div>
  </div>
);
